import React, { useEffect, useRef, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import {
  getLegalEntity,
  getOrgLegalEntity,
  isAddressLinkedToLicence,
} from 'requests/legalEntities';
import {
  deleteLegalEntityAttachment,
  downloadLegalEntityAttachment,
  previewLegalEntityAttachment,
} from 'requests/attachment';
import { getLicenceAttachmentFileData } from 'requests/licence';
import {
  ELegalAttachmentsGroupBy,
  ILegalAttachmentItem,
  ILegalEntityDeactivationWarnings,
} from 'models/legalEntity';
import { ILegalEntityData, LegalEntityDetails } from 'components/organisms/LegalEntityDetails';
import { Box } from '@mui/material';
import { Accordion } from 'components/molecules/Accordion';
import { FirearmSearchForm } from 'pages/LegalEntitiesPage/FirearmSearchForm';
import { useQuery } from 'hooks/useQuery';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import {
  IOrgLegalEntityData,
  OrgLegalEntityDetails,
} from 'components/organisms/OrgLegalEntityDetails';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';

import {
  ELicenceStatusIds,
  groupedAttachmentDetailTableColumns,
  groupedLegalEntityAttachmentsTableColumns,
  handleAddressDeletionCheck,
  legalEntityAddressTableColumns,
  legalEntityAncillariesTableColumns,
  legalEntityAncillaryGroupsTableColumns,
  legalEntityAttachmentsTableColumns,
  legalEntityContactTableColumns,
  legalEntityFirearmsTableColumns,
  legalEntityGroupsTableColumns,
  legalEntityPreviousNamesSurnamesTableColumns,
  mapGroupedAttachmentDetailsToDataSource,
  mapLEAddressToDataSource,
  mapLEAncillaryGropsToDataSource,
  mapLEAncillaryToDataSource,
  mapLEAttachmentsToDataSource,
  mapLEContactToDataSource,
  mapLEFirearmToDataSource,
  mapLEGroupToDataSource,
  mapLEPreviousNamesSurnamesToDataSource,
} from 'pages/LegalEntitiesPage/helpers';

import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { LegalEntityAddressTableCard } from 'pages/LegalEntitiesPage/LegalEntityAddressTableCard';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { CommentAccordion } from 'components/molecules/CommentAccordion';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Loader } from 'components/atoms/Loader';
import { LegalEntityFirearmTableCard } from 'pages/LegalEntitiesPage/LegalEntityFirearmTableCard';
import { TableTopButtons } from 'components/atoms/TableTopButtons';
import { Table } from 'components/molecules/Table';

import {
  renderDataColor,
  renderRowColor,
  renderRowColorHover,
} from 'components/molecules/Table/helpers';
import { Pagination } from 'components/molecules/Pagination';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { EPerPages, ISortOptions, ITableDataSource } from 'models/table';
import { EDictionaryPrefixTypesUniqueId, parseDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { CardList } from 'components/molecules/CardList';
import { LegalEntityGroupTableCard } from 'pages/LegalEntitiesPage/LegalEntityGroupTableCard';
import { GroupSearchForm } from 'pages/LegalEntitiesPage/GroupSearchForm';
import {
  initialGroupSearchFormValues,
  initialLegalAttachmentsSearchFormValues,
  ISearchFirearmFormValues,
  ISearchGroupFormValues,
  ISearchLegalAttachmentsFormValues,
  ISearchLegalEntityAncillariesFormValues,
  ISearchLegalEntityAncillaryGroupFormValues,
} from 'models/form';
import { useTranslations } from 'hooks/useTranslations';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { LegalEntityContactTableCard } from 'pages/LegalEntitiesPage/LegalEntityContactTableCard';
import { useLang } from 'models/langContext';
import { EArtAncillaryType } from 'constants/ArtAccessoryType';
import { StyledButton } from 'pages/Firearms/ViewRegisteredFirearmPage/styles';
import { isStringEmpty } from 'utils/string';
import { appendExtensionToFilename, downloadUrlAsFile } from 'utils/files';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { ActionTypes } from 'state/actions/alert';
import { useAccount } from 'pages/AccountPage/hooks';
import { useAuth } from 'models/authContext';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { FormModal } from 'components/molecules/Modal';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { AttachmentsSearchForm } from 'pages/LegalEntitiesPage/AttachmentsSearchForm';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { getDateFromISO } from 'utils/date';
import { LegalEntityAncillaryTableCard } from '../LegalEntityAncillaryTableCard';
import { AncillariesSearchForm } from '../AncillariesSearchForm';
import { AncillaryGroupsSearchForm } from '../AncillaryGroupsSearchForm';
import { LegalEntityAncillaryGroupTableCard } from '../LegalEntityAncillaryGroupTableCard';
import { LegalEntityAttachmentTableCard } from '../LegalEntityAttachmentTableCard';
import { LegalEntityGroupedAttachmentTableCard } from '../LegalEntityGroupedAttachmentTableCard';
import { LegalEntityPreviousNamesSurnamesTableCard } from '../LegalEntityPreviousNamesSurnamesTableCard';
import { StyledNoComments } from '../styles';
import { getGalleryMain, getGalleryMainPreview } from 'requests/gallery';
import { ESize } from 'models/galery';
import { ActivationDeactivationLE } from 'components/organisms/LegalEntityDetails/activationDeactivationLE';
import { ELegalStatus } from 'constants/LegalStatus';
import LegalEntityLicencesSection from './LegalEntityLicencesSection';
import LegalEntityCasesSection from './LegalEntityCasesSection';
import { downloadAttachmentFile, previewAttachmentFile } from 'requests/case';
import { IDictionary } from 'models/dictionary';

export const getAncillaryType = (typeId: string) => {
  switch (typeId) {
    case EArtAncillaryType.ACCESSORIES:
      return 'accessory';
    case EArtAncillaryType.AMMUNITION:
      return 'ammunition';
    case EArtAncillaryType.COMPONENTS:
      return 'component';
    default:
      return '';
  }
};

const NoComments = () => {
  const { t } = useTranslations();
  return <StyledNoComments>{t('legalEntityDetails.noComments.label')}</StyledNoComments>;
};

const ViewLegalEntityPage = () => {
  const { id } = useParams<{ id: string }>();
  const { selectedLanguage } = useLang();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const query = useQuery();
  const navigate = useNavigate();
  const { hasPermissions } = usePermission();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openLegalAttachmentDelete, setOpenLegalAttachmentDelete] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<string>();
  const [showLicences, setShowLicences] = useState(false);
  const [showCases, setShowCases] = useState(false);
  const [showDetailsCases, setShowDetailsCases] = useState(false);

  const {
    region,
    country,
    phoneCode,
    addressType,
    weaponLicenceStatus,
    weaponLicenceCaseStatus,
    attachmentDocumentTypeData,
    getRegion,
    getCountry,
    getAddressType,
    getPhoneCode,
    getWeaponLicenceStatus,
    getWeaponLicenceCaseStatus,
    getAttachmentDocumentTypeData,
    getMake,
    make,
    getModel,
    model,
    getArtComponentManufacturer,
    getArtAccessoryManufacturerFlashEliminator,
    getArtAccessoryManufacturerLaserLightModule,
    getArtAccessoryManufacturerMagazine,
    getArtAccessoryManufacturerOpticalSight,
    getArtAccessoryManufacturerSuppressor,
    getArtAmmunitionManufacturer,
    artComponentManufacturer,
    artAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerMagazine,
    artAccessoryManufacturerOpticalSight,
    artAccessoryManufacturerSuppressor,
    artAmmunitionManufacturer,
    getArtAccessoryCalibre,
    getArtAmmunitionCalibreGauge,
    getArtComponentCalibre,
    artAccessoryCalibre,
    artAmmunitionCalibreGauge,
    artComponentCalibre,
    getState,
    state,
  } = useDictionary();
  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>();
  const [isLegalEntityLoading, setIsLegalEntityLoading] = useState<boolean>(false);
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>();
  const [isOrgLegalEntityLoading, setIsOrgLegalEntityLoading] = useState<boolean>(false);
  const [isActiveStatus, setIsActiveStatus] = useState(false);
  const [manufacturer, setManufacturer] = useState<IDictionary[]>([]);
  const [caliber, setCaliber] = useState<IDictionary[]>([]);

  useEffect(() => {
    const manufactures = [
      artComponentManufacturer,
      artAccessoryManufacturerFlashEliminator,
      artAccessoryManufacturerLaserLightModule,
      artAccessoryManufacturerMagazine,
      artAccessoryManufacturerOpticalSight,
      artAccessoryManufacturerSuppressor,
      artAmmunitionManufacturer,
    ].map((data) => (data && data.length ? [...data] : []));

    setManufacturer(manufactures.flat());
  }, [
    artComponentManufacturer,
    artAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerMagazine,
    artAccessoryManufacturerOpticalSight,
    artAccessoryManufacturerSuppressor,
    artAmmunitionManufacturer,
  ]);
  useEffect(() => {
    const calibres = [artAccessoryCalibre, artAmmunitionCalibreGauge, artComponentCalibre].map(
      (data) => (data && data.length ? [...data] : []),
    );

    setCaliber(calibres.flat());
  }, [artAccessoryCalibre, artAmmunitionCalibreGauge, artComponentCalibre]);

  const isCivilianLE =
    legalEntity?.prefixGroupUniqueId === EDictionaryPrefixTypesUniqueId.civilianPrefix;
  const isCivilianOrg =
    orgLegalEntity?.organisationCategoryUniqueId === EOrganisationCategory.civilian;

  const [isLegalEntityCommentsLoading, setIsLegalEntityCommentsLoading] =
    useState<boolean>(true);
  const [isLegalEntityContactsLoading, setIsLegalEntityContactsLoading] =
    useState<boolean>(false);
  const [
    isLegalEntityPreviousNamesSurnamesLoading,
    setIsLegalEntityPreviousNamesSurnamesLoading,
  ] = useState(false);
  const [isLegalEntityAddressLoading, setIsLegalEntityAddressLoading] =
    useState<boolean>(false);
  const [currentPageLEComments, setCurrentPageLEComments] = useState<number>(1);
  const [perPageLEComments, setPerPageLEComments] = useState<EPerPages>(EPerPages.perPage25);
  const [isLegalEntityGroupLoading, setIsLegalEntityGroupLoading] = useState<boolean>(false);
  const [isLegalEntityAncillaryGroupLoading, setIsLegalEntityAncillaryGroupLoading] =
    useState<boolean>(true);
  const {
    legalEntityAlert,
    legalEntityOrgAlert,
    nextPageAlert,
    attachmentAlert,
    clearAlert,
    setAlert,
    legalEntityActivateAlert,
    legalEntityDeactivationWarningsAlert,
    ancillaryAlert,
    firearmAlert,
    addressDeletionAlert,
  } = useAlert();
  const {
    legalEntityFirearms,
    legalEntityAncillaries,
    legalEntityAncillaryGroups,
    paginatorAncillaries,
    paginatorAncillaryGroups,
    getLegalEntityFirearmsData,
    getLegalEntityAncillariesData,
    getLegalEntityAncillaryGroupsData,
    paginatorLEF,
    paginatorLEG,
    legalEntityGroups,
    getLegalEntityGroupsData,
    getLegalEntityAddressesData,
    legalEntityAddress,
    legalEntityContacts,
    legalEntityPreviousNamesSurnames,
    getLegalEntityContactsData,
    getLegalEntityCommentsData,
    getLegalEntityPreviousNamesSurnamesData,
    legalEntityComments,
    paginatorLEComments,
    getLegalEntityAttachmentsData,
    legalEntityAttachments,
    paginatorLegalAttachments,
  } = useLegalEntities();
  const { isMobile, isDesktop } = useThemeBreakpoint();
  const { isLicenceAuthorityValid, isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const { shortDateFormat, getProperties } = useGlobalProperty();

  const {
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isShowHidden: false,
    },
  });

  const type: string | null = query.get('type')?.trim() || null;

  const [currentPageLEF, setCurrentPageLEF] = useState<number>(1);
  const [perPageLEF, setPerPageLEF] = useState<EPerPages>(EPerPages.perPage25);
  const [firearmQuery, setFirearmQuery] = useState<Partial<ISearchFirearmFormValues>>({});
  const [firearmSort, setFirearmSort] = useState<ISortOptions | null>(null);

  const [currentPageAncillaries, setCurrentPageAncillaries] = useState<number>(1);
  const [perPageAncillaries, setPerPageAncillaries] = useState<EPerPages>(EPerPages.perPage25);
  const [ancillariesQuery, setAncillariesQuery] = useState<
    Partial<ISearchLegalEntityAncillariesFormValues>
  >({});
  const [currentPageAncillaryGroups, setCurrentPageAncillaryGroups] = useState<number>(1);
  const [perPageAncillaryGroups, setPerPageAncillaryGroups] = useState<EPerPages>(
    EPerPages.perPage25,
  );
  const [ancillaryGroupsQuery, setAncillaryGroupsQuery] = useState<
    Partial<ISearchLegalEntityAncillaryGroupFormValues>
  >({});
  const [ancillariesSort, setAncillariesSort] = useState<ISortOptions | null>(null);
  const [ancillaryGroupsSort, setAncillaryGroupsSort] = useState<ISortOptions | null>(null);

  const [isFirearmPaginationLoading, setIsFirearmPaginationLoading] = useState(false);
  const [isFirearmGroupPaginationLoading, setIsFirearmGroupPaginationLoading] =
    useState(false);
  const [isAncillaryPaginationLoading, setIsAncillaryPaginationLoading] = useState(false);
  const [isAncillaryGroupPaginationLoading, setIsAncillaryGroupPaginationLoading] =
    useState(false);
  const [isCommentPaginationLoading, setIsCommentPaginationLoading] = useState(false);

  const [currentPageLEG, setCurrentPageLEG] = useState<number>(1);
  const [perPageLEG, setPerPageLEG] = useState<EPerPages>(EPerPages.perPage25);
  const [groupQuery, setGroupQuery] = useState<Partial<ISearchGroupFormValues>>({});
  const [groupSort, setGroupSort] = useState<ISortOptions | null>(null);
  const [addressSort, setAddressSort] = useState<ISortOptions | null>(null);
  const [contactSort, setContactSort] = useState<ISortOptions | null>(null);
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);
  const [isFirearmSortLoaded, setIsFirearmSortLoaded] = useState<boolean>(true);
  const [isGroupSortLoaded, setIsGroupSortLoaded] = useState<boolean>(true);
  const [isAncillariesSortLoaded, setIsAncillariesSortLoaded] = useState<boolean>(true);
  const [isAncillaryGroupsSortLoaded, setIsAncillaryGroupsSortLoaded] =
    useState<boolean>(true);
  const [isCommentsPaginationVisible, setIsCommentsPaginationVisible] = useState(true);
  const [isContactSortLoaded, setIsContactSortLoaded] = useState(true);
  const [isAddressSortLoaded, setIsAddressSortLoaded] = useState(true);
  const [isFirearmQueryLoading, setIsFirearmQueryLoading] = useState<boolean>(false);
  const [isFirearmInitDataLoading, setIsFirearmInitDataLoading] = useState<boolean>(true);
  const [currentPageAttachments, setCurrentPageAttachments] = useState<number>(1);
  const [perPageAttachments, setPerPageAttachments] = useState<EPerPages>(EPerPages.perPage25);
  const [attachmentsQuery, setAttachmentsQuery] = useState<
    Partial<ISearchLegalAttachmentsFormValues>
  >(initialLegalAttachmentsSearchFormValues);
  const [attachmentsSort, setAttachmentsSort] = useState<ISortOptions | null>(null);
  const [isAttachmentQueryLoading, setIsAttachmentQueryLoading] = useState<boolean>(false);
  const [attachmentOpenDetail, setAttachmentOpenDetail] = useState<string>('');
  const [isGroupQueryLoading, setIsGroupQueryLoading] = useState<boolean>(false);
  const [isGroupQuerySearchButtonClicked, setIsGroupQuerySearchButtonClicked] =
    useState<boolean>(false);
  const [isAncillariesQueryLoading, setIsAncillariesQueryLoading] = useState<boolean>(false);
  const [isAncillariesDataLoading, setIsAncillariesDataLoading] = useState<boolean>(true);
  const [isAncillaryGroupsQueryLoading, setIsAncillaryGroupsQueryLoading] =
    useState<boolean>(false);
  const [isDeactivateLegalEntityModal, setIsDeactivateLegalEntityModal] = useState(false);
  const [isActivateLegalEntityModal, setIsActivateLegalEntityModal] = useState(false);
  const { t } = useTranslations();

  const previousFirearmSortRef = useRef(firearmSort);
  const scrollPosition = useRef(0);

  const { account, getSelfAccountData } = useAccount();
  const { user } = useAuth();

  const [image, setImage] = useState<any>(null);

  useEffect(() => {
    if (
      legalEntity &&
      hasWeaponLicenceModule() &&
      type === ELegalEntityTypes.individual &&
      legalEntity.prefixGroupUniqueId === EDictionaryPrefixTypesUniqueId.civilianPrefix &&
      !image
    ) {
      (async function getLegalEntityPhotoInit() {
        const photo = await getGalleryMainPreview(legalEntity.uniqueId, ESize.THUMBNAIL);
        const photoData = await getGalleryMain(legalEntity.uniqueId);
        if (photo && photoData) {
          setImage(photo);
        }
      })();
    }
  }, [legalEntity, type]);

  useEffect(() => {
    (async function getAccountDataInit() {
      if (user?.userId) {
        await getSelfAccountData();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    const show =
      (isCivilianLE || isCivilianOrg) &&
      hasWeaponLicenceModule() &&
      (hasPermissions(
        [EPermission.VIEW_GLOBAL_LICENCES, EPermission.VIEW_RESTRICTED_LICENCES],
        true,
      ) ||
        (hasPermissions([EPermission.VIEW_LICENCES_LE]) &&
          account?.assignedLegalIds.includes(id as string)));

    setShowLicences(!!show);
  }, [account, hasPermissions, isCivilianLE, isCivilianOrg]);

  useEffect(() => {
    const isCivilianAndLicence = (isCivilianLE || isCivilianOrg) && hasWeaponLicenceModule();
    const show =
      isCivilianAndLicence &&
      areAssetsVisible(
        legalEntity || orgLegalEntity,
        EPermission.VIEW_CASES_LE,
        EPermission.VIEW_GLOBAL_CASES,
        EPermission.VIEW_RESTRICTED_CASES,
      );
    const showDetails =
      isCivilianAndLicence &&
      hasPermissions([EPermission.VIEW_GLOBAL_CASES, EPermission.VIEW_RESTRICTED_CASES], true);

    setShowDetailsCases(!!showDetails);
    setShowCases(!!show);
  }, [account, hasPermissions, isCivilianLE, isCivilianOrg]);

  useEffect(() => {
    if (
      isAncillariesSortLoaded &&
      isAddressSortLoaded &&
      isFirearmSortLoaded &&
      isGroupSortLoaded &&
      isAncillaryGroupsSortLoaded &&
      isContactSortLoaded
    ) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [
    isAncillariesSortLoaded,
    isAddressSortLoaded,
    isFirearmSortLoaded,
    isGroupSortLoaded,
    isAncillaryGroupsSortLoaded,
    isContactSortLoaded,
  ]);

  const areAssetsVisible = (
    legal: ILegalEntityData | IOrgLegalEntityData | null | undefined,
    viewAssignedPermission: EPermission,
    viewLimitedPermission: EPermission,
    viewAllPermission: EPermission,
  ) => {
    if (!legal) {
      return false;
    }
    if (hasPermissions(viewAllPermission)) {
      return true;
    }
    if (hasPermissions(viewLimitedPermission)) {
      if (legal.visibilityUniqueId === ELegalVisibility.PUBLIC) {
        return true;
      }
      return account?.assignedLegalIds.includes(legal.uniqueId);
    }
    if (hasPermissions(viewAssignedPermission)) {
      return account?.assignedLegalIds.includes(legal.uniqueId);
    }
    return false;
  };

  useEffect(() => {
    (async function getLegalEntitiesFirearmsDataInit() {
      if (
        areAssetsVisible(
          legalEntity || orgLegalEntity,
          EPermission.VIEW_FIREARMS_LE,
          EPermission.VIEW_FIREARMS,
          EPermission.VIEW_ALL_FIREARMS,
        )
      ) {
        if (firearmSort !== previousFirearmSortRef.current) {
          setIsFirearmSortLoaded(false);
        }
        setIsFirearmPaginationLoading(true);
        await getLegalEntityFirearmsData(
          currentPageLEF,
          perPageLEF,
          { ...firearmQuery, legalUniqueId: id },
          firearmSort,
        );
        setIsFirearmInitDataLoading(false);
        setIsFirearmQueryLoading(false);
        setIsFirearmPaginationLoading(false);
        setIsFirearmSortLoaded(true);
        previousFirearmSortRef.current = firearmSort;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    currentPageLEF,
    perPageLEF,
    firearmQuery,
    firearmSort,
    legalEntity,
    orgLegalEntity,
    account,
  ]);

  const previousAncillariesSortRef = useRef(ancillariesSort);

  useEffect(() => {
    (async function getLegalEntityAncillariesDataInit() {
      if (
        areAssetsVisible(
          legalEntity || orgLegalEntity,
          EPermission.VIEW_ANCILLARIES_LE,
          EPermission.VIEW_LIMITED_ANCILLARIES,
          EPermission.VIEW_ALL_ANCILLARIES,
        )
      ) {
        if (ancillariesSort !== previousAncillariesSortRef.current) {
          setIsAncillariesSortLoaded(false);
        }
        setIsAncillaryPaginationLoading(true);
        await getLegalEntityAncillariesData(
          id as string,
          currentPageAncillaries,
          perPageAncillaries,
          ancillariesQuery,
          ancillariesSort,
        );
        setIsAncillariesDataLoading(false);
        setIsAncillariesQueryLoading(false);
        setIsAncillaryPaginationLoading(false);
        setIsAncillariesSortLoaded(true);
        previousAncillariesSortRef.current = ancillariesSort;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    currentPageAncillaries,
    perPageAncillaries,
    ancillariesQuery,
    ancillariesSort,
    legalEntity,
    orgLegalEntity,
    account,
  ]);

  const previousAncillariesGroupsSortRef = useRef(ancillaryGroupsSort);

  useEffect(() => {
    (async function getLegalEntityAncillaryGroupsDataInit() {
      if (
        areAssetsVisible(
          legalEntity || orgLegalEntity,
          EPermission.VIEW_ANCILLARIES_LE,
          EPermission.VIEW_LIMITED_ANCILLARIES,
          EPermission.VIEW_ALL_ANCILLARIES,
        )
      ) {
        if (ancillaryGroupsSort !== previousAncillariesGroupsSortRef.current) {
          setIsAncillaryGroupsSortLoaded(false);
        }
        setIsAncillaryGroupPaginationLoading(true);
        await getLegalEntityAncillaryGroupsData(
          id as string,
          currentPageAncillaryGroups,
          perPageAncillaryGroups,
          ancillaryGroupsQuery,
          ancillaryGroupsSort,
        );
      }
      setIsLegalEntityAncillaryGroupLoading(false);
      setIsAncillaryGroupsQueryLoading(false);
      setIsAncillaryGroupPaginationLoading(false);
      setIsAncillaryGroupsSortLoaded(true);
      previousAncillariesGroupsSortRef.current = ancillaryGroupsSort;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    currentPageAncillaryGroups,
    perPageAncillaryGroups,
    ancillaryGroupsQuery,
    ancillaryGroupsSort,
    legalEntity,
    orgLegalEntity,
    account,
  ]);

  const prepareQueryFromParams = (initialValues: any) => {
    const queryFromParams = { ...initialValues };
    let overrideGroupQuery = false;
    Object.getOwnPropertyNames(queryFromParams).forEach((key) => {
      const keyInParams = searchParams.get(key);
      if (keyInParams) {
        queryFromParams[key as keyof typeof queryFromParams] = keyInParams;
        overrideGroupQuery = true;
      }
    });
    if (overrideGroupQuery) {
      return queryFromParams;
    }
    return false;
  };

  const previousGroupSortRef = useRef(groupSort);

  useEffect(() => {
    (async function getLegalEntitiesGroupsDataInit() {
      if (
        areAssetsVisible(
          legalEntity || orgLegalEntity,
          EPermission.VIEW_FIREARMS_LE,
          EPermission.VIEW_FIREARMS,
          EPermission.VIEW_ALL_FIREARMS,
        )
      ) {
        const groupQueryFromParams = prepareQueryFromParams(initialGroupSearchFormValues);
        if (!isGroupQuerySearchButtonClicked && groupQueryFromParams) {
          setGroupQuery(groupQueryFromParams);
        }
        setIsGroupQuerySearchButtonClicked(false);

        if (groupSort !== previousGroupSortRef.current) {
          setIsGroupSortLoaded(false);
        }
        setIsFirearmGroupPaginationLoading(true);
        if (!legalEntityGroups.length) {
          setIsLegalEntityGroupLoading(true);
        }
        await getLegalEntityGroupsData(
          id as string,
          currentPageLEG,
          perPageLEG,
          groupQuery,
          groupSort,
        );
        setIsGroupQueryLoading(false);
        setIsFirearmGroupPaginationLoading(false);
        setIsLegalEntityGroupLoading(false);
        setIsGroupSortLoaded(true);
        previousGroupSortRef.current = groupSort;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    currentPageLEG,
    perPageLEG,
    groupQuery,
    groupSort,
    legalEntity,
    orgLegalEntity,
    account,
  ]);

  const isShowHidden: boolean = watch('isShowHidden');

  useEffect(() => {
    (async function getLegalEntitiesDataInit() {
      setIsCommentsPaginationVisible(false);
      if (
        (!!legalEntity || !!orgLegalEntity) &&
        id &&
        hasPermissions([EPermission.MANAGE_COMMENTS_LE])
      ) {
        setIsCommentPaginationLoading(true);
        await getLegalEntityCommentsData(id, {
          withHidden: isShowHidden,
          currentPage: currentPageLEComments,
          perPage: perPageLEComments,
        });

        setIsLegalEntityCommentsLoading(false);
        setIsCommentPaginationLoading(false);
        setIsCommentsPaginationVisible(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    isShowHidden,
    currentPageLEComments,
    perPageLEComments,
    legalEntity,
    orgLegalEntity,
  ]);

  const getLegalEntityData = () => {
    if (type === ELegalEntityTypes.individual) {
      (async function getLegalEntityDataInit() {
        setIsLegalEntityLoading(true);
        if (id) {
          const response = await getLegalEntity(id);
          if (response) {
            setLegalEntity(response);
          }
          setIsLegalEntityLoading(false);
        }
      })();
      setOrgLegalEntity(null);
    } else if (type === ELegalEntityTypes.organisation) {
      (async function getLegalEntityDataInit() {
        setIsOrgLegalEntityLoading(true);
        if (id) {
          const response = await getOrgLegalEntity(id);
          if (response) {
            setOrgLegalEntity(response);
          }
          setIsOrgLegalEntityLoading(false);
        }
      })();
      setLegalEntity(null);
    }
  };

  useEffect(() => {
    getLegalEntityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const previousAddressSortRef = useRef(addressSort);

  useEffect(() => {
    (async function getLegalEntityAddressInit() {
      if ((!!legalEntity || !!orgLegalEntity) && id) {
        if (!legalEntityAddress.length) {
          setIsLegalEntityAddressLoading(true);
        }
        if (addressSort !== previousAddressSortRef.current) {
          setIsAddressSortLoaded(false);
        }
        await getLegalEntityAddressesData(id, addressSort);
        setIsLegalEntityAddressLoading(false);
        setIsAddressSortLoaded(true);
        previousAddressSortRef.current = addressSort;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, addressSort, legalEntity, orgLegalEntity]);

  const previousContactSortRef = useRef(contactSort);

  useEffect(() => {
    (async function getLegalEntityContactsInit() {
      if ((!!legalEntity || !!orgLegalEntity) && id) {
        if (!legalEntityContacts.length) {
          setIsLegalEntityContactsLoading(true);
        }
        if (contactSort !== previousContactSortRef.current) {
          setIsContactSortLoaded(false);
        }
        await getLegalEntityContactsData(id, contactSort);
        setIsLegalEntityContactsLoading(false);
        setIsContactSortLoaded(true);
        previousContactSortRef.current = contactSort;
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, contactSort, legalEntity, orgLegalEntity]);

  useEffect(() => {
    (async function getLegalEntityPreviousNamesSurnamesInit() {
      if ((!!legalEntity || !!orgLegalEntity) && id) {
        if (!legalEntityPreviousNamesSurnames.length) {
          setIsLegalEntityPreviousNamesSurnamesLoading(true);
        }
        await getLegalEntityPreviousNamesSurnamesData(id);
        setIsLegalEntityPreviousNamesSurnamesLoading(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, legalEntity, orgLegalEntity]);

  useEffect(() => {
    getRegion();
    getAddressType();
    getCountry();
    getPhoneCode();
    getWeaponLicenceStatus();
    getWeaponLicenceCaseStatus();
    getAttachmentDocumentTypeData();
    getMake();
    getModel();
    getArtComponentManufacturer();
    getArtAccessoryManufacturerFlashEliminator();
    getArtAccessoryManufacturerLaserLightModule();
    getArtAccessoryManufacturerMagazine();
    getArtAccessoryManufacturerOpticalSight();
    getArtAccessoryManufacturerSuppressor();
    getArtAmmunitionManufacturer();
    getArtAccessoryCalibre();
    getArtAmmunitionCalibreGauge();
    getArtComponentCalibre();
    getState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (id && (legalEntity || orgLegalEntity)) {
      if (!legalEntityAttachments.length) {
        setIsAttachmentQueryLoading(true);
      }
      getLegalEntityAttachmentsData(
        id,
        currentPageAttachments,
        perPageAttachments,
        attachmentsQuery,
        attachmentsSort,
      ).finally(() => {
        setIsAttachmentQueryLoading(false);
      });
    }
  }, [
    id,
    currentPageAttachments,
    perPageAttachments,
    attachmentsQuery,
    attachmentsSort,
    legalEntity,
    orgLegalEntity,
    getLegalEntityAttachmentsData,
  ]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_LEGAL_ENTITY_ALERT);
      clearAlert(ActionTypes.CLEAR_LEGAL_ENTITY_ORG_ALERT);
      clearAlert(ActionTypes.CLEAR_ANCILLARY_ALERT);
      clearAlert(ActionTypes.CLEAR_FIREARM_ALERT);
      clearAlert(ActionTypes.CLEAR_ADDRESS_DELETION_ALERT);
      clearAlert(ActionTypes.CLEAR_LEGAL_ENTITY_ACTIVATE_ALERT);
      clearAlert(ActionTypes.CLEAR_LEGAL_ENTITY_DEACTIVATION_WARNINGS_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const licenceAuthorityValid = orgLegalEntity
    ? isLicenceAuthorityValid(
        orgLegalEntity?.primaryWeaponLicencingAuthorityUniqueId,
        orgLegalEntity?.secondaryWeaponLicencingAuthorityUniqueId,
      )
    : isLicenceAuthorityValid(
        legalEntity?.primaryWeaponLicencingAuthorityUniqueId,
        legalEntity?.secondaryWeaponLicencingAuthorityUniqueId,
      );

  const showActionFirearms = hasPermissions([EPermission.VIEW_FIREARMS]);

  const showActionFirearmsGroup = hasPermissions([EPermission.MANAGE_GROUPS_LE]);

  const handleAddFirearm = (item: ITableDataSource) => {
    if (item.hasDifferentStates) {
      setAlert(ActionTypes.SET_FIREARM_ALERT, {
        text: t('groupDetails.addToGroup.groupHasElementsWithDifferentStates.message'),
        variant: EAlertVariants.warning,
      } as IAlert);
    } else {
      navigate(
        `${ERouteLinks.LegalEntityGroupFirearmSearch.replace(
          ':id',
          id as string,
        )}?type=${type}&expand=groups&groupId=${item.id}`,
        { state: { isDetailsContext: true } },
      );
    }
  };

  const handleAddAncillary = (item: ITableDataSource) => {
    if (item.hasDifferentStates) {
      setAlert(ActionTypes.SET_ANCILLARY_ALERT, {
        text: t('groupDetails.addToGroup.groupHasElementsWithDifferentStates.message'),
        variant: EAlertVariants.warning,
      } as IAlert);
    } else {
      navigate(
        `${ERouteLinks.AddToAncillaryGroup.replace(':groupId', item.id as string).replace(
          ':id',
          id as string,
        )}?type=${type}&expand=ancillaryGroups`,
      );
    }
  };

  const handleLegalAttachmentView = async (
    legalUniqueId: string,
    attachmentUniqueId: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => {
    try {
      let url;
      if (licenceUniqueId) {
        url = await getLicenceAttachmentFileData(licenceUniqueId, attachmentUniqueId, 'view');
      } else if (caseUniqueId) {
        url = await previewAttachmentFile(attachmentUniqueId);
      } else {
        url = await previewLegalEntityAttachment(legalUniqueId, attachmentUniqueId);
      }

      if (url) {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Error viewing attachment', err);
    }
  };

  const handleLegalAttachmentDownload = async (
    legalUniqueId: string,
    attachmentUniqueId: string,
    fileName: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => {
    try {
      let url;
      if (licenceUniqueId) {
        url = await getLicenceAttachmentFileData(
          licenceUniqueId,
          attachmentUniqueId,
          'download',
        );
      } else if (caseUniqueId) {
        url = await downloadAttachmentFile(attachmentUniqueId);
      } else {
        url = await downloadLegalEntityAttachment(legalUniqueId, attachmentUniqueId);
      }

      if (url) {
        downloadUrlAsFile(url, fileName);
      }
    } catch (err) {
      console.error('Error downloading attachment', err);
    }
  };

  const handleLegalAttachmentDelete = async (attachmentUniqueId: string) => {
    setAttachmentToDelete(attachmentUniqueId);
    setOpenLegalAttachmentDelete(true);
  };
  const legalAttachmentDelete = async () => {
    if (id && attachmentToDelete)
      try {
        const result = await deleteLegalEntityAttachment(id, attachmentToDelete);
        if (result) {
          const currentEntity = legalEntity || orgLegalEntity;
          if (currentEntity) {
            if (type === ELegalEntityTypes.individual) {
              const updatedData = await getLegalEntity(currentEntity.uniqueId);
              setLegalEntity(updatedData);
            } else {
              const updatedData = await getOrgLegalEntity(currentEntity.uniqueId);
              setOrgLegalEntity(updatedData);
            }
          }
          await setAlert(ActionTypes.SET_ATTACHMENT_ALERT, {
            text: t('deleteAttchment.attachmentRemoved.text'),
          });
        }
        setAttachmentToDelete(undefined);
      } catch (err) {
        console.error('Error deleting attachment', err);
      }
  };

  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [missingFieldsModalOpen, setMissingFieldsModalOpen] = useState(false);

  useEffect(() => {
    if (
      legalEntity?.statusUniqueId === ELegalStatus.ACTIVE ||
      orgLegalEntity?.statusUniqueId === ELegalStatus.ACTIVE
    ) {
      setIsActiveStatus(true);
    } else {
      setIsActiveStatus(false);
    }
  }, [legalEntity, orgLegalEntity]);

  useEffect(() => {
    if (legalEntity || orgLegalEntity) {
      const warnings =
        type === ELegalEntityTypes.individual
          ? legalEntity?.warnings
          : orgLegalEntity?.warnings;
      const alertMessage = getAlertMessage(warnings);
      if (alertMessage)
        setAlert(
          ActionTypes.SET_LEGAL_ENTITY_DEACTIVATION_WARNINGS_ALERT,
          { text: t(alertMessage), variant: EAlertVariants.warning },
          true,
        );
      else clearAlert(ActionTypes.SET_LEGAL_ENTITY_DEACTIVATION_WARNINGS_ALERT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntity, orgLegalEntity, type, selectedLanguage]);

  const getAlertMessage = (warnings?: ILegalEntityDeactivationWarnings) => {
    if (!warnings) return undefined;
    if (warnings.hasActiveLicence && warnings.hasAssignedAssets)
      return 'legalEntityDetails.deactivatedWarning.assetsAndActiveLicences.text';
    if (warnings.hasActiveLicence)
      return 'legalEntityDetails.deactivatedWarning.activeLicences.text';
    if (warnings.hasAssignedAssets) return 'legalEntityDetails.deactivatedWarning.assets.text';
    return undefined;
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
          {
            label:
              type === ELegalEntityTypes.individual
                ? t('legalEntities.individual.accordion.label')
                : t('legalEntities.organization.accordion.label'),
            route: `${ERouteLinks.LegalEntities}?expand=${
              type === ELegalEntityTypes.individual ? 'individual' : 'organisation'
            }`,
          },
          t('legalEntityDetails.legalEntityDetails.text'),
        ]}
      />

      <FormModal
        open={openLegalAttachmentDelete}
        onClickClose={() => {
          setOpenLegalAttachmentDelete(false);
          setAttachmentToDelete(undefined);
        }}
        onClickApprove={() => {
          legalAttachmentDelete();
          setOpenLegalAttachmentDelete(false);
        }}
        title={t('legalEntityDetails.attachmentDelete.modal.title')}
        text={t('legalEntityDetails.attachmentDelete.modal.text')}
        approveButtonLabel="general.confirm.label"
      />

      {(legalEntity || orgLegalEntity) && hasPermissions([EPermission.MANAGE_LE]) && (
        <ActivationDeactivationLE
          handleCloseDeactivation={() => setIsDeactivateLegalEntityModal(false)}
          handleCloseLEActivation={() => setIsActivateLegalEntityModal(false)}
          openLEActivation={isActivateLegalEntityModal}
          openLEDeactivation={isDeactivateLegalEntityModal}
          version={legalEntity?.version || orgLegalEntity?.version || ''}
          legalEntityUpdate={getLegalEntityData}
        />
      )}
      {legalEntityActivateAlert && (
        <Box sx={{ mb: 1, mt: 2 }}>
          <Alert
            text={legalEntityActivateAlert.text}
            variant={legalEntityActivateAlert.variant}
          />
        </Box>
      )}
      {legalEntityDeactivationWarningsAlert && (
        <Box sx={{ mb: 2 }}>
          <Alert
            text={legalEntityDeactivationWarningsAlert.text}
            variant={legalEntityDeactivationWarningsAlert.variant}
          />
        </Box>
      )}
      {type === ELegalEntityTypes.individual && (
        <div>
          {isLegalEntityLoading ? (
            <Loader isLoading={isLegalEntityLoading} />
          ) : (
            legalEntity && (
              <LegalEntityDetails
                legalEntity={legalEntity}
                handleOpenDeactivate={() => setIsDeactivateLegalEntityModal(true)}
                handleOpenActivate={() => setIsActivateLegalEntityModal(true)}
                hasManagePermission={!!hasPermissions([EPermission.MANAGE_LE])}
              />
            )
          )}
        </div>
      )}
      {type === ELegalEntityTypes.organisation && (
        <div>
          {isOrgLegalEntityLoading ? (
            <Loader isLoading={isOrgLegalEntityLoading} />
          ) : (
            orgLegalEntity && (
              <OrgLegalEntityDetails
                legalEntity={orgLegalEntity}
                handleOpenDeactivate={() => setIsDeactivateLegalEntityModal(true)}
                handleOpenActivate={() => setIsActivateLegalEntityModal(true)}
                hasManagePermission={!!hasPermissions([EPermission.MANAGE_LE])}
              />
            )
          )}
        </div>
      )}
      {legalEntityOrgAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={legalEntityOrgAlert.text} variant={legalEntityOrgAlert.variant} />
        </Box>
      )}
      {legalEntityAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={legalEntityAlert.text} variant={legalEntityAlert.variant} />
        </Box>
      )}

      {nextPageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}
      <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', my: 2 }}>
        <StyledButton
          label={
            isAllExpanded
              ? t('transactionDetails.collapseAll.link')
              : t('transactionDetails.expandAll.link')
          }
          onClick={() => setIsAllExpanded((prev) => !prev)}
          variant={EButtonVariants.outlined}
          size={EButtonSizes.small}
        />
      </Grid>

      <Grid sx={{ width: !isDesktop ? 'calc(100vw - 48px)' : '100%' }}>
        {(legalEntity || orgLegalEntity) && showLicences && (
          <LegalEntityLicencesSection
            showLicences={showLicences}
            id={id as string}
            type={type as string}
            isAllExpanded={isAllExpanded}
            t={t}
            licenceAuthorityValid={licenceAuthorityValid}
            isDesktop={isDesktop}
            weaponLicenceStatus={weaponLicenceStatus}
            attachmentDocumentTypeData={attachmentDocumentTypeData}
            shortDateFormat={shortDateFormat}
            scrollPosition={scrollPosition}
            legalEntity={legalEntity}
            orgLegalEntity={orgLegalEntity}
            image={image}
          />
        )}
        {(legalEntity || orgLegalEntity) && showCases && (
          <LegalEntityCasesSection
            id={id as string}
            showDetailsCases={showDetailsCases}
            type={type as string}
            isAllExpanded={isAllExpanded}
            isDesktop={isDesktop}
            shortDateFormat={shortDateFormat}
            scrollPosition={scrollPosition}
            legalEntity={legalEntity}
            orgLegalEntity={orgLegalEntity}
          />
        )}
        {areAssetsVisible(
          legalEntity,
          EPermission.VIEW_FIREARMS_LE,
          EPermission.VIEW_FIREARMS,
          EPermission.VIEW_ALL_FIREARMS,
        ) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.firearm.accordion.label')}
              name="firearms"
              id="legal_entity_firearms"
              expanded={isAllExpanded}
            >
              <FirearmSearchForm
                onSubmit={(it) => {
                  setIsFirearmQueryLoading(true);
                  setFirearmQuery(it);
                }}
                isSearchButtonDisabled={isFirearmQueryLoading}
                isIndividualLE
              />
              {hasPermissions([EPermission.ADD_TRANSACTION]) &&
                licenceAuthorityValid &&
                isActiveStatus && (
                  <TableTopButtons>
                    <Button
                      label={t('legalEntityDetails.firearm.accordion.addFirearm.button')}
                      variant={EButtonVariants.contained}
                      id="legal_entity_individual-add_firearm_button"
                      icon={EIconTypes.plus}
                      onClick={() =>
                        navigate(
                          `${ERouteLinks.LegalEntityAddFirearm.replace(
                            ':id',
                            id as string,
                          )}?type=${type}&expand=firearms`,
                        )
                      }
                      sx={{ ml: 1 }}
                    />
                  </TableTopButtons>
                )}
              {isFirearmQueryLoading || isFirearmInitDataLoading ? (
                <Loader isLoading={true} />
              ) : (
                <>
                  {isDesktop ? (
                    <Table
                      columns={legalEntityFirearmsTableColumns({
                        renderStateFieldColor: (data: ITableDataSource) =>
                          renderDataColor(data).stateField,
                        type,
                        showAction: showActionFirearms,
                        t,
                        hasWeaponLicenceModule: hasWeaponLicenceModule(),
                      })}
                      dataSource={mapLEFirearmToDataSource(
                        legalEntityFirearms,
                        make,
                        model,
                        manufacturer,
                        caliber,
                        state,
                      )}
                      onSort={(it) => {
                        setFirearmSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isFirearmSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEFirearmToDataSource(
                        legalEntityFirearms,
                        make,
                        model,
                        manufacturer,
                        caliber,
                        state,
                      )}
                      render={(data, index) => (
                        <LegalEntityFirearmTableCard
                          data={data}
                          key={index}
                          renderColor={renderDataColor}
                          showAction={showActionFirearms}
                        />
                      )}
                    />
                  )}

                  {paginatorLEF && (
                    <Pagination
                      count={paginatorLEF?.totalElements}
                      perPage={perPageLEF}
                      onChangePage={(page) => {
                        setCurrentPageLEF(page);
                      }}
                      onChangePerPage={(value) => {
                        setPerPageLEF(value);
                      }}
                      current={currentPageLEF}
                      isLoading={isFirearmPaginationLoading}
                      isVisible={isFirearmSortLoaded}
                    />
                  )}
                </>
              )}
            </Accordion>
          </Grid>
        )}

        {areAssetsVisible(
          orgLegalEntity,
          EPermission.VIEW_FIREARMS_LE,
          EPermission.VIEW_FIREARMS,
          EPermission.VIEW_ALL_FIREARMS,
        ) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.firearm.accordion.label')}
              name="firearms"
              id="legal_entity_firearms"
              expanded={isAllExpanded}
            >
              <FirearmSearchForm
                onSubmit={(it) => {
                  setIsFirearmQueryLoading(true);
                  setFirearmQuery(it);
                }}
                isSearchButtonDisabled={isFirearmQueryLoading}
              />
              {hasPermissions([EPermission.ADD_TRANSACTION]) &&
                licenceAuthorityValid &&
                isActiveStatus && (
                  <TableTopButtons>
                    <Button
                      label={t('legalEntityDetails.firearm.accordion.addFirearm.button')}
                      variant={EButtonVariants.contained}
                      icon={EIconTypes.plus}
                      id="legal_entity_organisation-add_firearm_button"
                      onClick={() =>
                        navigate(
                          `${ERouteLinks.LegalEntityAddFirearm.replace(
                            ':id',
                            id as string,
                          )}?type=${type}&expand=firearms`,
                        )
                      }
                      sx={{ ml: 1 }}
                    />
                  </TableTopButtons>
                )}
              {isFirearmQueryLoading || isFirearmInitDataLoading ? (
                <Loader isLoading={true} />
              ) : (
                <>
                  {isDesktop ? (
                    <Table
                      columns={legalEntityFirearmsTableColumns({
                        renderStateFieldColor: (data: ITableDataSource) =>
                          renderDataColor(data).stateField,
                        type,
                        showAction: showActionFirearms,
                        t,
                        hasWeaponLicenceModule: hasWeaponLicenceModule(),
                      })}
                      dataSource={mapLEFirearmToDataSource(
                        legalEntityFirearms,
                        make,
                        model,
                        manufacturer,
                        caliber,
                        state,
                      )}
                      onSort={(it) => {
                        setFirearmSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isFirearmSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEFirearmToDataSource(
                        legalEntityFirearms,
                        make,
                        model,
                        manufacturer,
                        caliber,
                        state,
                      )}
                      render={(data, index) => (
                        <LegalEntityFirearmTableCard
                          data={data}
                          key={index}
                          renderColor={renderDataColor}
                          showAction={showActionFirearms}
                        />
                      )}
                    />
                  )}

                  {paginatorLEF && (
                    <Pagination
                      count={paginatorLEF?.totalElements}
                      perPage={perPageLEF}
                      onChangePage={(page) => {
                        setCurrentPageLEF(page);
                      }}
                      onChangePerPage={(value) => {
                        setPerPageLEF(value);
                      }}
                      current={currentPageLEF}
                      isLoading={isFirearmPaginationLoading}
                      isVisible={isFirearmSortLoaded}
                    />
                  )}
                </>
              )}
            </Accordion>
          </Grid>
        )}

        {areAssetsVisible(
          legalEntity,
          EPermission.VIEW_FIREARMS_LE,
          EPermission.VIEW_FIREARMS,
          EPermission.VIEW_ALL_FIREARMS,
        ) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.firearmGroups.accordion.label')}
              name="groups"
              expanded={isAllExpanded}
            >
              <GroupSearchForm
                initialFormValues={groupQuery}
                onSubmit={(it) => {
                  setIsGroupQueryLoading(true);
                  setIsGroupQuerySearchButtonClicked(true);
                  setGroupQuery(it);
                }}
                isSearchButtonDisabled={isGroupQueryLoading}
                isAddButtonHidden={!licenceAuthorityValid || !isActiveStatus}
              />
              <TableTopButtons />
              {isLegalEntityGroupLoading ? (
                <Loader isLoading={isLegalEntityGroupLoading} />
              ) : (
                <div>
                  {isDesktop ? (
                    <Table
                      columns={legalEntityGroupsTableColumns({
                        handleAdd: handleAddFirearm,
                        handleEdit: (groupId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityEditGroup.replace(
                              ':id',
                              id as string,
                            ).replace(':groupId', groupId)}?type=${type}&expand=groups`,
                          ),
                        handleDelete: (groupId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityDeleteGroup.replace(
                              ':id',
                              id as string,
                            ).replace(':groupId', groupId)}?type=${type}&expand=groups`,
                          ),
                        renderStateFieldColor: (data: ITableDataSource) =>
                          renderDataColor(data).stateField,
                        type,
                        id,
                        showAction: showActionFirearmsGroup,
                        isAnyLicenceAuthorityValid,
                      })}
                      dataSource={mapLEGroupToDataSource(legalEntityGroups)}
                      onSort={(it) => {
                        setGroupSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isGroupSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEGroupToDataSource(legalEntityGroups)}
                      render={(data, index) => (
                        <LegalEntityGroupTableCard
                          data={data}
                          key={index}
                          handleAddFirearm={handleAddFirearm}
                          renderDataColor={() => renderDataColor(data)}
                          showAction={showActionFirearmsGroup}
                          showModifyAction={isAnyLicenceAuthorityValid(
                            data.licenceAuthorityIds,
                          )}
                        />
                      )}
                    />
                  )}

                  {paginatorLEG && (
                    <Pagination
                      count={paginatorLEG?.totalElements}
                      perPage={perPageLEG}
                      onChangePage={(page) => {
                        setCurrentPageLEG(page);
                      }}
                      onChangePerPage={(value) => {
                        setPerPageLEG(value);
                      }}
                      current={currentPageLEG}
                      isLoading={isFirearmGroupPaginationLoading}
                      isVisible={isGroupSortLoaded}
                    />
                  )}
                </div>
              )}
              {firearmAlert && (
                <Box sx={{ marginBottom: '10px', mt: 2 }}>
                  <Alert text={firearmAlert.text} variant={firearmAlert.variant} />
                </Box>
              )}
            </Accordion>
          </Grid>
        )}

        {areAssetsVisible(
          orgLegalEntity,
          EPermission.VIEW_FIREARMS_LE,
          EPermission.VIEW_FIREARMS,
          EPermission.VIEW_ALL_FIREARMS,
        ) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.firearmGroups.accordion.label')}
              name="groups"
              expanded={isAllExpanded}
            >
              <GroupSearchForm
                initialFormValues={groupQuery}
                onSubmit={(it) => {
                  setIsGroupQueryLoading(true);
                  setIsGroupQuerySearchButtonClicked(true);
                  setGroupQuery(it);
                }}
                isSearchButtonDisabled={isGroupQueryLoading}
                isAddButtonHidden={!licenceAuthorityValid || !isActiveStatus}
              />
              <TableTopButtons />
              {isLegalEntityGroupLoading ? (
                <Loader isLoading={isLegalEntityGroupLoading} />
              ) : (
                <div>
                  {isDesktop ? (
                    <Table
                      columns={legalEntityGroupsTableColumns({
                        handleAdd: handleAddFirearm,
                        handleEdit: (groupId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityEditGroup.replace(
                              ':id',
                              id as string,
                            ).replace(':groupId', groupId)}?type=${type}&expand=groups`,
                          ),
                        handleDelete: (groupId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityDeleteGroup.replace(
                              ':id',
                              id as string,
                            ).replace(':groupId', groupId)}?type=${type}&expand=groups`,
                          ),
                        renderStateFieldColor: (data: ITableDataSource) =>
                          renderDataColor(data).stateField,
                        type,
                        id,
                        showAction: showActionFirearmsGroup,
                        isAnyLicenceAuthorityValid,
                      })}
                      dataSource={mapLEGroupToDataSource(legalEntityGroups)}
                      onSort={(it) => {
                        setGroupSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isGroupSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEGroupToDataSource(legalEntityGroups)}
                      render={(data, index) => (
                        <LegalEntityGroupTableCard
                          data={data}
                          key={index}
                          handleAddFirearm={handleAddFirearm}
                          renderDataColor={() => renderDataColor(data)}
                          showAction={showActionFirearmsGroup}
                        />
                      )}
                    />
                  )}

                  {paginatorLEG && (
                    <Pagination
                      count={paginatorLEG?.totalElements}
                      perPage={perPageLEG}
                      onChangePage={(page) => {
                        setCurrentPageLEG(page);
                      }}
                      onChangePerPage={(value) => {
                        setPerPageLEG(value);
                      }}
                      current={currentPageLEG}
                      isLoading={isFirearmPaginationLoading}
                      isVisible={isGroupSortLoaded}
                    />
                  )}
                </div>
              )}
              {firearmAlert && (
                <Box sx={{ marginBottom: '10px', mt: 2 }}>
                  <Alert text={firearmAlert.text} variant={firearmAlert.variant} />
                </Box>
              )}
            </Accordion>
          </Grid>
        )}

        {areAssetsVisible(
          legalEntity || orgLegalEntity,
          EPermission.VIEW_ANCILLARIES_LE,
          EPermission.VIEW_LIMITED_ANCILLARIES,
          EPermission.VIEW_ALL_ANCILLARIES,
        ) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.ancillaries.accordion.label')}
              name="ancillaries"
              id="legal_entity_ancillaries"
              expanded={isAllExpanded}
            >
              <>
                <AncillariesSearchForm
                  onSubmit={(it) => {
                    setIsAncillariesQueryLoading(true);
                    setAncillariesQuery(it);
                  }}
                  isSearchButtonDisabled={isAncillariesQueryLoading}
                  isIndividualLE={legalEntity !== undefined && legalEntity !== null}
                />
                {hasPermissions([EPermission.ADD_TRANSACTION]) &&
                  licenceAuthorityValid &&
                  isActiveStatus && (
                    <TableTopButtons>
                      <Button
                        id="legal_entity_details_ancillaries-add-button"
                        label={t('legalEntityDetails.firearm.accordion.addAncillary.button')}
                        variant={EButtonVariants.contained}
                        icon={EIconTypes.plus}
                        onClick={() =>
                          navigate(
                            `${ERouteLinks.LegalEntityAddAncillary.replace(
                              ':id',
                              id as string,
                            )}?type=${type}&expand=ancillaries`,
                          )
                        }
                        sx={{ ml: 1 }}
                      />
                    </TableTopButtons>
                  )}

                {isAncillariesQueryLoading || isAncillariesDataLoading ? (
                  <Loader isLoading={true} />
                ) : (
                  <>
                    {isDesktop ? (
                      <Table
                        columns={legalEntityAncillariesTableColumns({
                          renderStateFieldColor: (data: ITableDataSource) =>
                            renderDataColor(data).stateField,
                          showDetail: hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES]),
                          t,
                        })}
                        dataSource={mapLEAncillaryToDataSource(
                          legalEntityAncillaries,
                          state,
                          manufacturer,
                          caliber,
                        )}
                        onSort={(it) => {
                          setAncillariesSort(it);
                          scrollPosition.current = window.scrollY;
                        }}
                        specificRowColor={renderRowColor}
                        specificRowColorHover={renderRowColorHover}
                        isSortLoaded={isAncillariesSortLoaded}
                      />
                    ) : (
                      <CardList
                        items={mapLEAncillaryToDataSource(
                          legalEntityAncillaries,
                          state,
                          manufacturer,
                          caliber,
                        )}
                        render={(data, index) => (
                          <LegalEntityAncillaryTableCard
                            data={data}
                            key={index}
                            showDetail={hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES])}
                          />
                        )}
                      />
                    )}

                    {paginatorAncillaries && (
                      <Pagination
                        count={paginatorAncillaries?.totalElements}
                        perPage={perPageAncillaries}
                        onChangePage={(page) => {
                          setCurrentPageAncillaries(page);
                        }}
                        onChangePerPage={(value) => {
                          setPerPageAncillaries(value);
                        }}
                        current={currentPageAncillaries}
                        isLoading={isAncillaryPaginationLoading}
                        isVisible={isAncillariesSortLoaded}
                      />
                    )}
                  </>
                )}
              </>
            </Accordion>
          </Grid>
        )}
        {areAssetsVisible(
          legalEntity || orgLegalEntity,
          EPermission.VIEW_ANCILLARIES_LE,
          EPermission.VIEW_LIMITED_ANCILLARIES,
          EPermission.VIEW_ALL_ANCILLARIES,
        ) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.ancillaryGroups.accordion.label')}
              name="ancillaryGroups"
              expanded={isAllExpanded}
            >
              <AncillaryGroupsSearchForm
                onSubmit={(it) => {
                  setIsAncillaryGroupsQueryLoading(true);
                  setAncillaryGroupsQuery(it);
                }}
                addGroup={() =>
                  navigate(
                    `${ERouteLinks.LegalEntityAddAncillaryGroup.replace(
                      ':id',
                      id as string,
                    )}?type=${type}&expand=ancillaryGroups`,
                  )
                }
                isSearchButtonDisabled={isAncillaryGroupsQueryLoading}
                isAddButtonHidden={!licenceAuthorityValid || !isActiveStatus}
              />
              <TableTopButtons />
              {isAncillaryGroupsQueryLoading || isLegalEntityAncillaryGroupLoading ? (
                <Loader isLoading={true} />
              ) : (
                <>
                  {isDesktop ? (
                    <Table
                      columns={legalEntityAncillaryGroupsTableColumns({
                        handleAddToGroup: handleAddAncillary,
                        handleEdit: (groupId: string) =>
                          navigate(
                            `${ERouteLinks.EditAncillaryGroup.replace(
                              ':id',
                              id as string,
                            ).replace(
                              ':groupId',
                              groupId,
                            )}?type=${type}&expand=ancillaryGroups`,
                          ),
                        handleDelete: (groupId: string) =>
                          navigate(
                            `${ERouteLinks.DeleteAncillaryGroup.replace(
                              ':id',
                              id as string,
                            ).replace(
                              ':groupId',
                              groupId,
                            )}?type=${type}&expand=ancillaryGroups`,
                          ),
                        renderStateFieldColor: (data: ITableDataSource) =>
                          renderDataColor(data).stateField,
                        type,
                        id,
                        showAction: hasPermissions([EPermission.MANAGE_GROUPS_LE]),
                        isAnyLicenceAuthorityValid,
                      })}
                      dataSource={mapLEAncillaryGropsToDataSource(legalEntityAncillaryGroups)}
                      onSort={(it) => {
                        setAncillaryGroupsSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isAncillaryGroupsSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEAncillaryGropsToDataSource(legalEntityAncillaryGroups)}
                      render={(data, index) => (
                        <LegalEntityAncillaryGroupTableCard
                          data={data}
                          key={index}
                          id={id}
                          renderStateFieldColor={(d: ITableDataSource) =>
                            renderDataColor(d).stateField
                          }
                          handleAddAncillary={handleAddAncillary}
                          legalEntityType={type || LEAccordionNames.INDIVIDUAL}
                          showAction={hasPermissions([EPermission.MANAGE_GROUPS_LE])}
                          showModifyAction={isAnyLicenceAuthorityValid(
                            data.licenceAuthorityIds,
                          )}
                        />
                      )}
                    />
                  )}

                  {paginatorAncillaryGroups && (
                    <Pagination
                      count={paginatorAncillaryGroups?.totalElements}
                      perPage={perPageAncillaryGroups}
                      onChangePage={(page) => {
                        setCurrentPageAncillaryGroups(page);
                      }}
                      onChangePerPage={(value) => {
                        setPerPageAncillaryGroups(value);
                      }}
                      current={currentPageAncillaryGroups}
                      isLoading={isAncillaryGroupPaginationLoading}
                      isVisible={isAncillaryGroupsSortLoaded}
                    />
                  )}
                </>
              )}
              {ancillaryAlert && (
                <Box sx={{ marginBottom: '10px', mt: 2 }}>
                  <Alert text={ancillaryAlert.text} variant={ancillaryAlert.variant} />
                </Box>
              )}
            </Accordion>
          </Grid>
        )}

        {!!legalEntity && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.comments.accordion.label')}
              name="comments"
              expanded={isAllExpanded}
            >
              <Grid
                container
                sx={{
                  marginBottom: { xs: '20px', sm: '16px' },
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormCheckbox
                    name="isShowHidden"
                    label={t('legalEntityDetails.commentsTab.displayDisabledComments.label')}
                    control={control}
                    errors={errors}
                    withValidation={false}
                  />
                </Grid>
                {hasPermissions([EPermission.MANAGE_COMMENTS_LE]) && licenceAuthorityValid && (
                  <Grid
                    size={{ xs: 12, sm: 6 }}
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <Button
                      label={t('legalEntityDetails.commentsTab.addComment.button')}
                      id="add-comment-button"
                      variant={EButtonVariants.contained}
                      icon={EIconTypes.plus}
                      onClick={() => {
                        const LEType: any = query.get('type');
                        const expandParam: any = 'comments';
                        const queryParams: any = new URLSearchParams({
                          type: LEType,
                          expand: expandParam,
                        });
                        navigate(
                          `${ERouteLinks.LegalEntityAddComment.replace(
                            ':id',
                            id as string,
                          )}?${queryParams.toString()}`,
                        );
                      }}
                      fullWidth={isMobile}
                    />
                  </Grid>
                )}
              </Grid>

              {isLegalEntityCommentsLoading ? (
                <Loader isLoading={true} />
              ) : (
                legalEntityComments?.map((item, index) => (
                  <Box key={item.uniqueId} sx={{ marginBottom: '8px' }}>
                    <CommentAccordion
                      comment={item}
                      name="comments"
                      visibilityButtonId={`legal-comment-visibility-button-${index}`}
                      dropdownIconId={`legal-comment-dropdown-${index}`}
                      deleteLink={
                        hasPermissions([EPermission.MANAGE_COMMENTS_LE]) &&
                        licenceAuthorityValid
                          ? ERouteLinks.LegalEntityDeleteComment
                          : undefined
                      }
                    />
                  </Box>
                ))
              )}
              {!isLegalEntityCommentsLoading && !legalEntityComments?.length && <NoComments />}
              {paginatorLEComments && (
                <Pagination
                  count={paginatorLEComments?.totalElements}
                  perPage={perPageLEComments}
                  onChangePage={(page) => {
                    setCurrentPageLEComments(page);
                  }}
                  onChangePerPage={(value) => {
                    setPerPageLEComments(value);
                  }}
                  current={currentPageLEComments}
                  isLoading={isCommentPaginationLoading}
                  isVisible={isCommentsPaginationVisible}
                />
              )}
            </Accordion>
          </Grid>
        )}

        {!!orgLegalEntity && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('legalEntityDetails.comments.accordion.label')}
              name="comments"
              expanded={isAllExpanded}
            >
              <Grid
                container
                sx={{
                  marginBottom: { xs: '20px', sm: '16px' },
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormCheckbox
                    name="isShowHidden"
                    label={t('legalEntityDetails.commentsTab.displayDisabledComments.label')}
                    control={control}
                    errors={errors}
                    withValidation={false}
                  />
                </Grid>
                {hasPermissions([EPermission.MANAGE_COMMENTS_LE]) && licenceAuthorityValid && (
                  <Grid
                    size={{ xs: 12, sm: 6 }}
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <Button
                      label={t('legalEntityDetails.commentsTab.addComment.button')}
                      id="add-comment-button"
                      variant={EButtonVariants.contained}
                      icon={EIconTypes.plus}
                      onClick={() => {
                        const LEType: any = query.get('type');
                        const expandParam: any = 'comments';
                        const queryParams: any = new URLSearchParams({
                          type: LEType,
                          expand: expandParam,
                        });
                        navigate(
                          `${ERouteLinks.LegalEntityAddComment.replace(
                            ':id',
                            id as string,
                          )}?${queryParams.toString()}`,
                        );
                      }}
                      fullWidth={isMobile}
                    />
                  </Grid>
                )}
              </Grid>

              {isLegalEntityCommentsLoading ? (
                <Loader isLoading={true} />
              ) : (
                legalEntityComments?.map((item) => (
                  <Box key={item.uniqueId} sx={{ marginBottom: '8px' }}>
                    <CommentAccordion
                      name="comments"
                      comment={item}
                      deleteLink={
                        hasPermissions([EPermission.MANAGE_COMMENTS_LE]) &&
                        licenceAuthorityValid
                          ? ERouteLinks.LegalEntityDeleteComment
                          : undefined
                      }
                    />
                  </Box>
                ))
              )}
              {!isLegalEntityCommentsLoading && !legalEntityComments?.length && <NoComments />}
              {paginatorLEComments && (
                <Pagination
                  count={paginatorLEComments?.totalElements}
                  perPage={perPageLEComments}
                  onChangePage={(page) => {
                    setCurrentPageLEComments(page);
                  }}
                  onChangePerPage={(value) => {
                    setPerPageLEComments(value);
                  }}
                  current={currentPageLEComments}
                  isLoading={isCommentPaginationLoading}
                  isVisible={isCommentsPaginationVisible}
                />
              )}
            </Accordion>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          {!!legalEntity && (
            <Accordion
              title={t('legalEntityDetails.contact.accordion.label')}
              name="contact"
              expanded={isAllExpanded}
            >
              <TableTopButtons>
                {hasPermissions([EPermission.MANAGE_LE]) && licenceAuthorityValid && (
                  <Button
                    id={`legal_entity_details_contact-add-${legalEntity.uniqueId}`}
                    label={t('legalEntityDetails.contactTab.addContact.button')}
                    variant={EButtonVariants.contained}
                    icon={EIconTypes.plus}
                    onClick={() =>
                      navigate(
                        `${ERouteLinks.LegalEntityAddContact.replace(
                          ':id',
                          id as string,
                        )}?type=${type}&expand=contact`,
                      )
                    }
                    sx={{ ml: 1 }}
                  />
                )}
              </TableTopButtons>

              {isLegalEntityContactsLoading ? (
                <Loader isLoading={isLegalEntityContactsLoading} />
              ) : (
                <div>
                  {isDesktop && type ? (
                    <Table
                      columns={legalEntityContactTableColumns({
                        type: type as ELegalEntityTypes,
                        showActions: licenceAuthorityValid,
                        account,
                        hasPermissions,
                        handleEdit: (contactId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityEditContact.replace(
                              ':id',
                              id as string,
                            ).replace(':contactId', contactId)}?type=${type}&expand=contact`,
                          ),
                        handleDelete: (contactId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityDeleteContact.replace(
                              ':id',
                              id as string,
                            ).replace(':contactId', contactId)}?type=${type}&expand=contact`,
                          ),
                      })}
                      dataSource={mapLEContactToDataSource(
                        legalEntityContacts,
                        parseDictionary(phoneCode, 'name'),
                      )}
                      onSort={(it) => {
                        setContactSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isContactSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEContactToDataSource(
                        legalEntityContacts,
                        parseDictionary(phoneCode, 'name'),
                      )}
                      render={(data, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <LegalEntityContactTableCard
                            data={data}
                            key={index}
                            showActions={licenceAuthorityValid}
                            account={account}
                          />
                        </Box>
                      )}
                    />
                  )}
                </div>
              )}
            </Accordion>
          )}

          {!!orgLegalEntity && (
            <Accordion
              title={t('legalEntityDetails.contact.accordion.label')}
              name="contact"
              expanded={isAllExpanded}
            >
              <TableTopButtons>
                {hasPermissions([EPermission.MANAGE_LE]) && licenceAuthorityValid && (
                  <Button
                    id={`legal_entity_details_contact-add-${orgLegalEntity.uniqueId}`}
                    label={t('legalEntityDetails.contactTab.addContact.button')}
                    variant={EButtonVariants.contained}
                    icon={EIconTypes.plus}
                    onClick={() =>
                      navigate(
                        `${ERouteLinks.LegalEntityAddContact.replace(
                          ':id',
                          id as string,
                        )}?type=${type}&expand=contact`,
                      )
                    }
                    sx={{ ml: 1 }}
                  />
                )}
              </TableTopButtons>

              {isLegalEntityContactsLoading ? (
                <Loader isLoading={isLegalEntityContactsLoading} />
              ) : (
                <div>
                  {isDesktop && type ? (
                    <Table
                      columns={legalEntityContactTableColumns({
                        type: type as ELegalEntityTypes,
                        account,
                        hasPermissions,
                        handleEdit: (contactId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityEditContact.replace(
                              ':id',
                              id as string,
                            ).replace(':contactId', contactId)}?type=${type}&expand=contact`,
                          ),
                        handleDelete: (contactId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityDeleteContact.replace(
                              ':id',
                              id as string,
                            ).replace(':contactId', contactId)}?type=${type}&expand=contact`,
                          ),
                        showActions: licenceAuthorityValid,
                      })}
                      dataSource={mapLEContactToDataSource(
                        legalEntityContacts,
                        parseDictionary(phoneCode, 'name'),
                      )}
                      onSort={(it) => {
                        setContactSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isContactSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEContactToDataSource(
                        legalEntityContacts,
                        parseDictionary(phoneCode, 'name'),
                      )}
                      render={(data, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <LegalEntityContactTableCard
                            data={data}
                            key={index}
                            showActions={licenceAuthorityValid}
                            account={account}
                          />
                        </Box>
                      )}
                    />
                  )}
                </div>
              )}
            </Accordion>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          {!!legalEntity && isCivilianLE && (
            <Accordion
              title={t('legalEntityDetails.previousNamesSurnames.accordion.label')}
              name="previousNamesSurnames"
              expanded={isAllExpanded}
            >
              <TableTopButtons>
                {hasPermissions([EPermission.MANAGE_LE]) && licenceAuthorityValid && (
                  <Button
                    id={`legal_entity_details_previous_names_surnames-add-${legalEntity.uniqueId}`}
                    label={t('legalEntityDetails.previousNamesSurnamesTab.addNew.button')}
                    variant={EButtonVariants.contained}
                    icon={EIconTypes.plus}
                    onClick={() =>
                      navigate(
                        `${ERouteLinks.LegalEntityAddPreviousNamesSurnames.replace(
                          ':id',
                          id as string,
                        )}?type=${type}&expand=previousNamesSurnames`,
                      )
                    }
                    sx={{ ml: 1 }}
                  />
                )}
              </TableTopButtons>

              {isLegalEntityPreviousNamesSurnamesLoading ? (
                <Loader isLoading={isLegalEntityPreviousNamesSurnamesLoading} />
              ) : (
                <div>
                  {isDesktop && type ? (
                    <Table
                      columns={legalEntityPreviousNamesSurnamesTableColumns({
                        handleEdit: (nameId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityEditPreviousNamesSurnames.replace(
                              ':id',
                              id as string,
                            ).replace(
                              ':previousNamesSurnamesId',
                              nameId,
                            )}?type=${type}&expand=previousNamesSurnames`,
                          ),
                        handleDelete: (nameId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityDeletePreviousNamesSurnames.replace(
                              ':id',
                              id as string,
                            ).replace(
                              ':previousNamesSurnamesId',
                              nameId,
                            )}?type=${type}&expand=previousNamesSurnames`,
                          ),
                        dateFormat: shortDateFormat,
                      })}
                      dataSource={mapLEPreviousNamesSurnamesToDataSource(
                        legalEntityPreviousNamesSurnames,
                      )}
                    />
                  ) : (
                    <CardList
                      items={mapLEPreviousNamesSurnamesToDataSource(
                        legalEntityPreviousNamesSurnames,
                      )}
                      render={(data, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <LegalEntityPreviousNamesSurnamesTableCard data={data} key={index} />
                        </Box>
                      )}
                    />
                  )}
                </div>
              )}
            </Accordion>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          {!!legalEntity && (
            <Accordion
              title={t('legalEntityDetails.address.accordion.label')}
              name="address"
              expanded={isAllExpanded}
            >
              <TableTopButtons>
                {hasPermissions([EPermission.MANAGE_LE]) && licenceAuthorityValid && (
                  <Button
                    id="legal_entity_details_individual_address-add"
                    label={t('legalEntityDetails.addressTab.addAddress.button')}
                    variant={EButtonVariants.contained}
                    icon={EIconTypes.plus}
                    onClick={() =>
                      navigate(
                        `${ERouteLinks.LegalEntityAddAddress.replace(
                          ':id',
                          id as string,
                        )}?type=${type}&expand=address`,
                      )
                    }
                    sx={{ ml: 1 }}
                  />
                )}
              </TableTopButtons>

              {addressDeletionAlert && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Alert
                    text={addressDeletionAlert.text}
                    variant={addressDeletionAlert.variant}
                  />
                </Box>
              )}

              {isLegalEntityAddressLoading ? (
                <Loader isLoading={isLegalEntityAddressLoading} />
              ) : (
                <div>
                  {isDesktop ? (
                    <Table
                      columns={legalEntityAddressTableColumns({
                        handleEdit: (addressId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityEditAddress.replace(
                              ':id',
                              id as string,
                            ).replace(':addressId', addressId)}?type=${type}&expand=address`,
                          ),
                        handleShowOnMap: (addressId: string) =>
                          navigate(
                            `${ERouteLinks.ViewLegalEntityAddressMap.replace(
                              ':id',
                              id as string,
                            ).replace(':addressId', addressId)}?type=${type}&expand=address`,
                          ),
                        handleDelete: async (addressId: string) => {
                          const linkResult = await isAddressLinkedToLicence(
                            id as string,
                            addressId,
                          );

                          const { shouldShowAlert, alertMessage } = handleAddressDeletionCheck(
                            addressId,
                            legalEntityAddress,
                            linkResult,
                          );

                          if (shouldShowAlert) {
                            setAlert(ActionTypes.SET_ADDRESS_DELETION_ALERT, {
                              text: t(alertMessage),
                              variant: EAlertVariants.warning,
                            } as IAlert);
                          } else {
                            navigate(
                              `${ERouteLinks.LegalEntityDeleteAddress.replace(
                                ':id',
                                id as string,
                              ).replace(':addressId', addressId)}?type=${type}&expand=address`,
                            );
                          }
                        },
                        t,
                        licenceAuthorityValid,
                      })}
                      dataSource={mapLEAddressToDataSource(
                        legalEntityAddress,
                        parseDictionary(region, 'name'),
                        parseDictionary(country, 'name'),
                        parseDictionary(addressType, 'name'),
                        id as string,
                      )}
                      onSort={(it) => {
                        setAddressSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isAddressSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEAddressToDataSource(
                        legalEntityAddress,
                        parseDictionary(region, 'name'),
                        parseDictionary(country, 'name'),
                        parseDictionary(addressType, 'name'),
                        id as string,
                      )}
                      render={(data, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <LegalEntityAddressTableCard
                            data={data}
                            key={index}
                            licenceAuthorityValid={licenceAuthorityValid}
                          />
                        </Box>
                      )}
                    />
                  )}
                </div>
              )}
            </Accordion>
          )}
          {!!orgLegalEntity && (
            <Accordion
              title={t('legalEntityDetails.address.accordion.label')}
              name="address"
              expanded={isAllExpanded}
            >
              <TableTopButtons>
                {hasPermissions([EPermission.MANAGE_LE]) && licenceAuthorityValid && (
                  <Button
                    id="legal_entity_details_organisation_address-add"
                    label={t('legalEntityDetails.addressTab.addAddress.button')}
                    variant={EButtonVariants.contained}
                    icon={EIconTypes.plus}
                    onClick={() =>
                      navigate(
                        `${ERouteLinks.LegalEntityAddAddress.replace(
                          ':id',
                          id as string,
                        )}?type=${type}&expand=address`,
                      )
                    }
                    sx={{ ml: 1 }}
                  />
                )}
              </TableTopButtons>

              {addressDeletionAlert && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Alert
                    text={addressDeletionAlert.text}
                    variant={addressDeletionAlert.variant}
                  />
                </Box>
              )}

              {isLegalEntityAddressLoading ? (
                <Loader isLoading={isLegalEntityAddressLoading} />
              ) : (
                <div>
                  {isDesktop ? (
                    <Table
                      columns={legalEntityAddressTableColumns({
                        handleEdit: (addressId: string) =>
                          navigate(
                            `${ERouteLinks.LegalEntityEditAddress.replace(
                              ':id',
                              id as string,
                            ).replace(':addressId', addressId)}?type=${type}&expand=address`,
                          ),
                        handleShowOnMap: (addressId: string) =>
                          navigate(
                            `${ERouteLinks.ViewLegalEntityAddressMap.replace(
                              ':id',
                              id as string,
                            ).replace(':addressId', addressId)}?type=${type}&expand=address`,
                          ),
                        handleDelete: async (addressId: string) => {
                          const linkResult = await isAddressLinkedToLicence(
                            id as string,
                            addressId,
                          );

                          const { shouldShowAlert, alertMessage } = handleAddressDeletionCheck(
                            addressId,
                            legalEntityAddress,
                            linkResult,
                          );

                          if (shouldShowAlert) {
                            setAlert(ActionTypes.SET_ADDRESS_DELETION_ALERT, {
                              text: t(alertMessage),
                              variant: EAlertVariants.warning,
                            } as IAlert);
                          } else {
                            navigate(
                              `${ERouteLinks.LegalEntityDeleteAddress.replace(
                                ':id',
                                id as string,
                              ).replace(':addressId', addressId)}?type=${type}&expand=address`,
                            );
                          }
                        },
                        t,
                        licenceAuthorityValid,
                      })}
                      dataSource={mapLEAddressToDataSource(
                        legalEntityAddress,
                        parseDictionary(region, 'name'),
                        parseDictionary(country, 'name'),
                        parseDictionary(addressType, 'name'),
                        id as string,
                      )}
                      onSort={(it) => {
                        setAddressSort(it);
                        scrollPosition.current = window.scrollY;
                      }}
                      specificRowColor={renderRowColor}
                      specificRowColorHover={renderRowColorHover}
                      isSortLoaded={isAddressSortLoaded}
                    />
                  ) : (
                    <CardList
                      items={mapLEAddressToDataSource(
                        legalEntityAddress,
                        parseDictionary(region, 'name'),
                        parseDictionary(country, 'name'),
                        parseDictionary(addressType, 'name'),
                        id as string,
                      )}
                      render={(data, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <LegalEntityAddressTableCard
                            data={data}
                            key={index}
                            licenceAuthorityValid={licenceAuthorityValid}
                          />
                        </Box>
                      )}
                    />
                  )}
                </div>
              )}
            </Accordion>
          )}
          {(legalEntity || orgLegalEntity) && (
            <Accordion
              title={t('legalEntityDetails.attachments.accordion.label')}
              name="attachments"
              expanded={isAllExpanded}
            >
              <AttachmentsSearchForm
                onSubmit={(it) => {
                  setIsAttachmentQueryLoading(true);
                  setAttachmentsQuery(it);
                  if (
                    it.groupBy === ELegalAttachmentsGroupBy.ATTACHMENT_TYPE ||
                    it.groupBy === ELegalAttachmentsGroupBy.NUMBER
                  ) {
                    setAttachmentsSort(null);
                  }
                }}
                licenceAuthorityValid={licenceAuthorityValid}
                isSearchButtonDisabled={isAttachmentQueryLoading}
                onAddAttachment={() =>
                  navigate(
                    `${ERouteLinks.LegalEntityAddAttachment.replace(
                      ':id',
                      id || '',
                    )}?type=${type}`,
                    {
                      state: { originalSearch: location.search },
                    },
                  )
                }
                showLicenceColumns={
                  !!hasWeaponLicenceModule() && (isCivilianLE || isCivilianOrg)
                }
              />
              {attachmentAlert && (
                <Box sx={{ mt: 2 }}>
                  <Alert text={attachmentAlert.text} variant={attachmentAlert.variant} />
                </Box>
              )}
              {isAttachmentQueryLoading ? (
                <Loader isLoading={isAttachmentQueryLoading} />
              ) : (
                <div>
                  {(() => {
                    const isGroupedView =
                      attachmentsQuery.groupBy === ELegalAttachmentsGroupBy.ATTACHMENT_TYPE ||
                      attachmentsQuery.groupBy === ELegalAttachmentsGroupBy.NUMBER;
                    const extendedSorting = isCivilianLE || isCivilianOrg;

                    if (isGroupedView && hasWeaponLicenceModule() && extendedSorting) {
                      return (
                        <Box sx={{ marginTop: '18px' }}>
                          {isDesktop ? (
                            <Table
                              columns={groupedLegalEntityAttachmentsTableColumns({
                                t,
                                setAttachmentOpenDetail,
                                attachmentOpenDetail,
                                attachmentDocumentTypeDictionary: parseDictionary(
                                  attachmentDocumentTypeData,
                                  'name',
                                ),
                                groupBy: attachmentsQuery.groupBy as
                                  | typeof ELegalAttachmentsGroupBy.ATTACHMENT_TYPE
                                  | typeof ELegalAttachmentsGroupBy.NUMBER,
                                type,
                                hasPermissionsLicence:
                                  showLicences &&
                                  !!hasPermissions(
                                    [
                                      EPermission.VIEW_GLOBAL_LICENCES,
                                      EPermission.VIEW_RESTRICTED_LICENCES,
                                    ],
                                    true,
                                  ),
                              })}
                              dataSource={(() => {
                                const groupedData = legalEntityAttachments as Record<
                                  string,
                                  ILegalAttachmentItem[]
                                >;

                                const sortedEntries = Object.entries(groupedData).sort(
                                  ([keyA], [keyB]) => {
                                    if (keyA === 'null') return 1;
                                    if (keyB === 'null') return -1;
                                    return keyA.localeCompare(keyB);
                                  },
                                );

                                return sortedEntries.map(([groupKey, attachments]) => {
                                  if (
                                    attachmentsQuery.groupBy ===
                                    ELegalAttachmentsGroupBy.ATTACHMENT_TYPE
                                  ) {
                                    const displayLabel =
                                      parseDictionary(attachmentDocumentTypeData, 'name')?.[
                                        groupKey
                                      ] || groupKey;

                                    return {
                                      id: groupKey,
                                      attachmentType: displayLabel,
                                      fileCount: attachments.length,
                                      detailsColumns: mapGroupedAttachmentDetailsToDataSource(
                                        attachments,
                                        parseDictionary(weaponLicenceStatus, 'name'),
                                        parseDictionary(weaponLicenceCaseStatus, 'name'),
                                        id,
                                        parseDictionary(attachmentDocumentTypeData, 'name'),
                                      ),
                                    };
                                  }
                                  const firstAttachment = attachments[0];
                                  let parentObjectStatusUniqueId = null;

                                  if (
                                    attachments.filter(
                                      (it) =>
                                        it.parentObjectStatusUniqueId ===
                                        ELicenceStatusIds.ACTIVE,
                                    ).length > 0
                                  ) {
                                    parentObjectStatusUniqueId = ELicenceStatusIds.ACTIVE;
                                  } else {
                                    parentObjectStatusUniqueId =
                                      attachments.sort((a, b) => {
                                        const dateA = getDateFromISO(a.createdDate) as Date;
                                        const dateB = getDateFromISO(b.createdDate) as Date;
                                        return dateA.getTime() - dateB.getTime();
                                      })[0]?.parentObjectStatusUniqueId || null;
                                  }

                                  return {
                                    id: groupKey,
                                    parentObjectIdNumber: groupKey === 'null' ? '-' : groupKey,
                                    licenceUniqueId: firstAttachment?.licenceUniqueId || null,
                                    caseUniqueId: firstAttachment?.caseUniqueId || null,
                                    legalUniqueId: id,
                                    licenceName: firstAttachment?.licenceName || '-',
                                    licenceCode: firstAttachment?.licenceCode || '-',
                                    licenceType: firstAttachment?.licenceName || '-',
                                    parentObjectStatusUniqueId,
                                    objectStatus: parentObjectStatusUniqueId
                                      ? parseDictionary(weaponLicenceStatus, 'name')?.[
                                          parentObjectStatusUniqueId
                                        ] ||
                                        parseDictionary(weaponLicenceCaseStatus, 'name')?.[
                                          parentObjectStatusUniqueId
                                        ]
                                      : '-',
                                    fileCount: attachments.length,
                                    detailsColumns: mapGroupedAttachmentDetailsToDataSource(
                                      attachments,
                                      parseDictionary(weaponLicenceStatus, 'name'),
                                      parseDictionary(weaponLicenceCaseStatus, 'name'),
                                      id,
                                      parseDictionary(attachmentDocumentTypeData, 'name'),
                                    ),
                                  };
                                });
                              })()}
                              detailId={attachmentOpenDetail}
                              detailsColumns={groupedAttachmentDetailTableColumns({
                                t,
                                dateFormat: shortDateFormat,
                                type,
                                handleView: handleLegalAttachmentView,
                                handleDownload: handleLegalAttachmentDownload,
                                handleDelete: handleLegalAttachmentDelete,
                                groupBy: attachmentsQuery.groupBy as
                                  | typeof ELegalAttachmentsGroupBy.ATTACHMENT_TYPE
                                  | typeof ELegalAttachmentsGroupBy.NUMBER,
                                attachmentDocumentTypeDictionary: parseDictionary(
                                  attachmentDocumentTypeData,
                                  'name',
                                ),
                                hasPermissionsAttachments: !!hasPermissions([
                                  EPermission.MANAGE_LE,
                                ]),
                                hasPermissionsLicence:
                                  showLicences &&
                                  !!hasPermissions(
                                    [
                                      EPermission.VIEW_GLOBAL_LICENCES,
                                      EPermission.VIEW_RESTRICTED_LICENCES,
                                    ],
                                    true,
                                  ),
                              })}
                            />
                          ) : (
                            <CardList
                              items={(() => {
                                const groupedData = legalEntityAttachments as Record<
                                  string,
                                  ILegalAttachmentItem[]
                                >;

                                const sortedEntries = Object.entries(groupedData).sort(
                                  ([keyA], [keyB]) => {
                                    if (keyA === 'null') return 1;
                                    if (keyB === 'null') return -1;
                                    return keyA.localeCompare(keyB);
                                  },
                                );

                                return sortedEntries.map(([groupKey, attachments]) => {
                                  if (
                                    attachmentsQuery.groupBy ===
                                    ELegalAttachmentsGroupBy.ATTACHMENT_TYPE
                                  ) {
                                    const displayLabel =
                                      parseDictionary(attachmentDocumentTypeData, 'name')?.[
                                        groupKey
                                      ] || groupKey;

                                    const mappedAttachments = attachments.map((att) => ({
                                      ...att,
                                      id: att.uniqueId,
                                      fileName: appendExtensionToFilename(
                                        att.fileName,
                                        att.fileType,
                                      ),
                                      legalUniqueId: id,
                                      attachmentType:
                                        parseDictionary(attachmentDocumentTypeData, 'name')?.[
                                          att.documentTypeUniqueId
                                        ] || att.documentTypeUniqueId,
                                      licenceStatusUniqueId: att.parentObjectStatusUniqueId,
                                      objectStatus: att.parentObjectStatusUniqueId
                                        ? parseDictionary(weaponLicenceStatus, 'name')?.[
                                            att.parentObjectStatusUniqueId
                                          ] ||
                                          parseDictionary(weaponLicenceCaseStatus, 'name')?.[
                                            att.parentObjectStatusUniqueId
                                          ]
                                        : '',
                                    }));

                                    return {
                                      groupKey,
                                      attachmentType: displayLabel,
                                      fileCount: attachments.length,
                                      attachments: mappedAttachments,
                                    };
                                  }
                                  const firstAttachment = attachments[0];
                                  const objectStatusName =
                                    firstAttachment?.parentObjectStatusUniqueId
                                      ? parseDictionary(weaponLicenceStatus, 'name')?.[
                                          firstAttachment.parentObjectStatusUniqueId
                                        ] ||
                                        parseDictionary(weaponLicenceCaseStatus, 'name')?.[
                                          firstAttachment.parentObjectStatusUniqueId
                                        ]
                                      : null;

                                  let licenceStatusUniqueId = null;

                                  if (
                                    attachments.filter(
                                      (it) =>
                                        it.parentObjectStatusUniqueId ===
                                        ELicenceStatusIds.ACTIVE,
                                    ).length > 0
                                  ) {
                                    licenceStatusUniqueId = ELicenceStatusIds.ACTIVE;
                                  } else {
                                    licenceStatusUniqueId =
                                      attachments.sort((a, b) => {
                                        const dateA = getDateFromISO(a.createdDate) as Date;
                                        const dateB = getDateFromISO(b.createdDate) as Date;
                                        return dateA.getTime() - dateB.getTime();
                                      })[0]?.parentObjectStatusUniqueId || null;
                                  }

                                  const mappedAttachments = attachments.map((att) => ({
                                    ...att,
                                    id: att.uniqueId,
                                    legalUniqueId: id,
                                    fileName: appendExtensionToFilename(
                                      att.fileName,
                                      att.fileType,
                                    ),
                                    attachmentType:
                                      parseDictionary(attachmentDocumentTypeData, 'name')?.[
                                        att.documentTypeUniqueId
                                      ] || att.documentTypeUniqueId,
                                    objectStatus: att.parentObjectStatusUniqueId
                                      ? parseDictionary(weaponLicenceStatus, 'name')?.[
                                          att.parentObjectStatusUniqueId
                                        ] ||
                                        parseDictionary(weaponLicenceCaseStatus, 'name')?.[
                                          att.parentObjectStatusUniqueId
                                        ]
                                      : '',
                                  }));

                                  return {
                                    groupKey,
                                    parentObjectIdNumber: groupKey === 'null' ? '-' : groupKey,
                                    licenceName: firstAttachment?.licenceName || '-',
                                    licenceCode: firstAttachment?.licenceCode || '-',
                                    objectStatus: objectStatusName || '-',
                                    licenceStatusUniqueId: licenceStatusUniqueId || '-',
                                    fileCount: attachments.length,
                                    attachments: mappedAttachments,
                                  };
                                });
                              })()}
                              render={(data, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                  <LegalEntityGroupedAttachmentTableCard
                                    data={data}
                                    groupBy={
                                      attachmentsQuery.groupBy as
                                        | typeof ELegalAttachmentsGroupBy.ATTACHMENT_TYPE
                                        | typeof ELegalAttachmentsGroupBy.NUMBER
                                    }
                                    handleView={handleLegalAttachmentView}
                                    handleDownload={handleLegalAttachmentDownload}
                                    handleDelete={handleLegalAttachmentDelete}
                                    type={type}
                                    hasPermissionsAttachments={
                                      !!hasPermissions([EPermission.MANAGE_LE])
                                    }
                                    hasPermissionsLicence={
                                      showLicences &&
                                      !!hasPermissions(
                                        [
                                          EPermission.VIEW_GLOBAL_LICENCES,
                                          EPermission.VIEW_RESTRICTED_LICENCES,
                                        ],
                                        true,
                                      )
                                    }
                                    t={t}
                                  />
                                </Box>
                              )}
                            />
                          )}
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ marginTop: '18px' }}>
                        {isDesktop ? (
                          <Table
                            columns={legalEntityAttachmentsTableColumns({
                              t,
                              showAction: true,
                              dateFormat: shortDateFormat,
                              type,
                              handleView: handleLegalAttachmentView,
                              handleDownload: handleLegalAttachmentDownload,
                              handleDelete: handleLegalAttachmentDelete,
                              isLicenceAuthorityValid: licenceAuthorityValid,
                              hasPermissionsAttachments: !!hasPermissions([
                                EPermission.MANAGE_LE,
                              ]),
                              hasPermissionsLicence:
                                showLicences &&
                                !!hasPermissions(
                                  [
                                    EPermission.VIEW_GLOBAL_LICENCES,
                                    EPermission.VIEW_RESTRICTED_LICENCES,
                                  ],
                                  true,
                                ),
                              showLicenceColumns:
                                !!hasWeaponLicenceModule() && extendedSorting,
                            })}
                            dataSource={mapLEAttachmentsToDataSource(
                              legalEntityAttachments as ILegalAttachmentItem[],
                              parseDictionary(attachmentDocumentTypeData, 'name'),
                              parseDictionary(weaponLicenceStatus, 'name'),
                              parseDictionary(weaponLicenceCaseStatus, 'name'),
                              id,
                            )}
                            onSort={(data: ISortOptions) => {
                              setAttachmentsSort(data);
                            }}
                          />
                        ) : (
                          <CardList
                            items={mapLEAttachmentsToDataSource(
                              legalEntityAttachments as ILegalAttachmentItem[],
                              parseDictionary(attachmentDocumentTypeData, 'name'),
                              parseDictionary(weaponLicenceStatus, 'name'),
                              parseDictionary(weaponLicenceCaseStatus, 'name'),
                              id,
                            )}
                            render={(data, index) => (
                              <Box key={index} sx={{ mb: 2 }}>
                                <LegalEntityAttachmentTableCard
                                  data={data}
                                  handleView={handleLegalAttachmentView}
                                  handleDownload={handleLegalAttachmentDownload}
                                  handleDelete={handleLegalAttachmentDelete}
                                  hasPermissionsAttachments={
                                    !!hasPermissions([EPermission.MANAGE_LE])
                                  }
                                  isLicenceAuthorityValid={licenceAuthorityValid}
                                  type={type}
                                  hasPermissionsLicence={
                                    showLicences &&
                                    !!hasPermissions(
                                      [
                                        EPermission.VIEW_GLOBAL_LICENCES,
                                        EPermission.VIEW_RESTRICTED_LICENCES,
                                      ],
                                      true,
                                    )
                                  }
                                  showLicenceColumns={
                                    !!hasWeaponLicenceModule() && extendedSorting
                                  }
                                  t={t}
                                  key={index}
                                />
                              </Box>
                            )}
                          />
                        )}
                      </Box>
                    );
                  })()}
                  {paginatorLegalAttachments && (
                    <Pagination
                      count={paginatorLegalAttachments?.totalElements}
                      onChangePage={(page) => setCurrentPageAttachments(page)}
                      current={currentPageAttachments}
                      perPage={perPageAttachments}
                      onChangePerPage={(value) => setPerPageAttachments(value)}
                      isLoading={false}
                    />
                  )}
                </div>
              )}
            </Accordion>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export { ViewLegalEntityPage };
